/**
 * /early-access — "The Story Behind the Resume" pre-launch landing page.
 * Movement-first: headline + story form (6 thoughtful questions) + consented
 * anonymized story wall + SPARK closing. ?src= / ?utm_source= tracked.
 */
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { CheckCircle2, Loader, Quote, Flame, ArrowDown, Zap, ArrowRight, Download } from "lucide-react";

const NAVY = "#0F1B2D";
const ORANGE = "#FF6A00";
const YELLOW = "#FFC145";
const CREAM = "#F6F1E7";

const QUESTIONS = [
  { key: "about",          label: "Tell us about yourself in a few sentences.", required: true,  rows: 3,
    placeholder: "Who you are, where you've been, where you're headed…" },
  { key: "career",         label: "What career are you pursuing?", required: true,  input: true,
    placeholder: "e.g., Product management, nursing, data engineering…" },
  { key: "resume_gap",     label: "What is one thing your resume doesn't fully capture?", required: true,  rows: 3,
    placeholder: "The resilience, the pivot, the comeback — the part between the bullet points." },
  { key: "accomplishment", label: "What accomplishment are you most proud of?", required: false, rows: 3,
    placeholder: "Optional — big or small, we want to hear it." },
  { key: "challenge",      label: "What challenge have you overcome that shaped your career?", required: false, rows: 3,
    placeholder: "Optional — the setback, the restart, the fight back." },
  { key: "wish_understood", label: "What do you wish employers understood about you before they met you?", required: false, rows: 3,
    placeholder: "Optional — if your resume could speak, what would it say?" },
];

const inputStyle = { borderColor: NAVY, background: "#FFFDF8", color: NAVY };
const labelCls = "font-mono-rm text-[10px] uppercase tracking-[2px] font-bold";

const timeAgo = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (!Number.isFinite(s) || s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const anOrA = (w) => (/^[aeiou]/i.test((w || "").trim()) ? "an" : "a");
const eventSentence = (ev) =>
  ev.kind === "fit_check"
    ? `Resmo just checked ${anOrA(ev.label)} ${ev.label}'s job fit ⚡`
    : `Resmo just read ${anOrA(ev.label)} ${ev.label}'s resume 👀`;

export default function EarlyAccess() {
  const [params] = useSearchParams();
  const src = params.get("src") || params.get("utm_source") || "";
  const [form, setForm] = useState({
    name: "", email: "", linkedin_url: "",
    about: "", career: params.get("career") || "", resume_gap: "", accomplishment: "", challenge: "", wish_understood: "",
  });
  const [wantsAccess, setWantsAccess] = useState(true);
  const [shareOk, setShareOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [wall, setWall] = useState([]);
  const [wallOffset, setWallOffset] = useState(0);
  const [challenge, setChallenge] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventIdx, setEventIdx] = useState(0);
  const formRef = useRef(null);

  const WALL_VISIBLE = 4;
  useEffect(() => {
    if (wall.length <= WALL_VISIBLE) return;
    const t = setInterval(() => setWallOffset((o) => (o + WALL_VISIBLE) % wall.length), 6000);
    return () => clearInterval(t);
  }, [wall.length]);
  const visibleWall = wall.length <= WALL_VISIBLE
    ? wall
    : Array.from({ length: WALL_VISIBLE }, (_, i) => wall[(wallOffset + i) % wall.length]);

  useEffect(() => {
    axios.get(`${API}/early-access/wall`)
      .then((r) => setWall(r.data?.stories || []))
      .catch(() => {});
    axios.get(`${API}/challenge/current`)
      .then((r) => setChallenge(r.data))
      .catch(() => {});
    const fetchActivity = () =>
      axios.get(`${API}/early-access/activity`)
        .then((r) => setEvents(r.data?.events || []))
        .catch(() => {});
    fetchActivity();
    const p = setInterval(fetchActivity, 45000);
    return () => clearInterval(p);
  }, []);

  useEffect(() => {
    if (events.length <= 1) return;
    const t = setInterval(() => setEventIdx((i) => i + 1), 4000);
    return () => clearInterval(t);
  }, [events.length]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/early-access/submit`, {
        ...form,
        linkedin_url: form.linkedin_url || null,
        wants_early_access: wantsAccess,
        share_ok: shareOk,
        source: src || "direct",
      });
      setDone(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something hiccuped — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="early-access-page">
      {/* Hero */}
      <div style={{ background: NAVY }} className="px-6 pt-12 pb-20 sm:pt-16 sm:pb-24">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-8">
          <div className="flex-1">
          <div className="font-mono-rm text-[10px] uppercase tracking-[3px] mb-8" style={{ color: YELLOW }}>
            · The Story Behind the Resume · A RESMO movement
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.08] tracking-tight text-white" data-testid="early-access-headline">
            Your resume opens the door.<br />
            <span style={{ color: ORANGE }}>Your story changes the conversation.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg max-w-2xl" style={{ color: "#94A3B8" }} data-testid="early-access-subheadline">
            Join the first wave of professionals helping reshape how careers are
            introduced, understood, and discovered.
          </p>
          <button
            onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 border-2 font-black text-sm uppercase tracking-[1.5px] transition-transform hover:-translate-y-0.5"
            style={{ background: ORANGE, borderColor: "white", color: "white", boxShadow: "4px 4px 0 0 rgba(255,255,255,0.25)" }}
            data-testid="early-access-cta-btn"
          >
            Tell Us Your Story <ArrowDown size={15} />
          </button>
          </div>
          <div className="flex-shrink-0 text-center" data-testid="early-access-hero-mascot-wrap">
            <div className="inline-block px-4 py-2 mb-3 text-xs font-bold"
                 style={{ background: CREAM, color: NAVY, border: `2px solid ${ORANGE}`, boxShadow: `3px 3px 0 0 ${ORANGE}` }}>
              Hi, I'm <span style={{ color: ORANGE }}>Resmo</span> — I'd love to hear your story.
            </div>
            <img
              src="/brand/resmo-welcoming.png"
              alt="Resmo, the RESMO mascot, welcoming you with open arms"
              className="w-44 sm:w-52 lg:w-64 mx-auto select-none"
              draggable={false}
              data-testid="early-access-hero-mascot"
            />
          </div>
        </div>
      </div>

      {/* Live activity ticker — anonymous social proof */}
      {events.length > 0 && (
        <div className="px-6 -mt-8 relative z-10" data-testid="early-access-activity-ticker">
          <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3"
               style={{ background: "white", border: `2px solid ${NAVY}`,
                        borderRadius: 8, boxShadow: `3px 3px 0 0 ${ORANGE}` }}>
            <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                    style={{ background: "#10B981" }} />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "#10B981" }} />
            </span>
            <span key={eventIdx} className="text-sm font-semibold truncate" style={{ color: NAVY }}
                  data-testid="early-access-activity-item">
              {eventSentence(events[eventIdx % events.length])}
              <span className="font-normal ml-2 text-xs" style={{ color: "#94A3B8" }}>
                {timeAgo(events[eventIdx % events.length].created_at)}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Manifesto strip */}
      <div className="max-w-3xl mx-auto px-6 py-12 text-center">
        <p className="text-base md:text-lg leading-relaxed" style={{ color: "#334155" }} data-testid="early-access-manifesto">
          Have you ever felt like your resume didn't tell your whole story?
          Maybe it didn't capture your <em>resilience</em>. Maybe it couldn't explain
          why you changed careers, took time away, or fought your way back after a setback.
        </p>
        <p className="mt-4 text-base md:text-lg font-bold" style={{ color: NAVY }}>
          Every resume is an introduction. But every introduction leaves parts of the story untold.
        </p>
        <p className="mt-4 text-sm" style={{ color: "#64748B" }}>
          That's why we're building RESMO — a Career Intelligence Companion designed to help
          people become seen, understood, and prepared for every step of their career journey.
          Before we launch, we want to hear from you.
        </p>
      </div>

        {/* This week's challenge teaser (hidden until admin flips the challenge live) */}
        {challenge && challenge.visible && (
        <div className="max-w-3xl mx-auto px-6 pb-2">
          <Link to="/challenge"
                className="flex flex-wrap items-center gap-3 border-[3px] px-5 py-4 bg-white transition-transform hover:-translate-y-0.5"
                style={{ borderColor: ORANGE, boxShadow: `5px 5px 0 0 ${NAVY}` }}
                data-testid="early-access-challenge-teaser">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 flex-shrink-0"
                  style={{ borderColor: ORANGE, color: ORANGE, background: "rgba(255,106,0,0.08)" }}>
              <Zap size={15} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-mono-rm text-[10px] uppercase tracking-[2px] font-bold" style={{ color: ORANGE }}>
                This week's career challenge · {challenge.label}
              </div>
              <div className="text-sm font-bold mt-0.5" style={{ color: NAVY }}>
                {challenge.challenge}
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 font-black text-xs uppercase tracking-[1.5px] flex-shrink-0"
                  style={{ color: NAVY }}>
              Take it · free <ArrowRight size={13} />
            </span>
          </Link>
        </div>
      )}

      {/* Story form */}
      <div ref={formRef} className="max-w-3xl mx-auto px-6 pb-14 scroll-mt-6">
        <div className="bg-white border-[3px] p-6 sm:p-8"
             style={{ borderColor: NAVY, boxShadow: `8px 8px 0 0 ${NAVY}` }}>
          {done ? (
            <div className="text-center py-8" data-testid="early-access-success">
              <img
                src="/brand/resmo-cheering.png"
                alt="Resmo cheering with both arms in the air"
                className="w-36 mx-auto mb-3 select-none animate-bounce"
                style={{ animationIterationCount: 4, animationDuration: "0.8s" }}
                draggable={false}
                data-testid="early-access-success-mascot"
              />
              <div className="font-mono-rm text-[10px] uppercase tracking-[3px] font-bold mb-2" style={{ color: ORANGE }}>
                · Resmo is cheering for you ·
              </div>
              <h2 className="text-lg md:text-lg font-black" style={{ color: NAVY }}>
                {done.already_on_list ? "You're already part of the movement — story updated." : "Thank you. Your story matters."}
              </h2>
              <p className="mt-3 text-sm max-w-md mx-auto" style={{ color: "#475569" }}>
                We read every single story — yours will help shape the future of career
                transformation. {wantsAccess && "Your early-access invite will land by email the moment your seat opens."}
              </p>
              <div className="mt-6 font-bold text-sm" style={{ color: NAVY }}>
                Beyond the Resume. Be Seen. Be Understood. Be Discovered.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="early-access-form">
              <h2 className="text-lg md:text-lg font-black" style={{ color: NAVY }}>
                Tell us your story
              </h2>
              <p className="mt-1 text-sm" style={{ color: "#475569" }}>
                Your answers could help shape the future of career transformation.
              </p>

              <div className="mt-6 space-y-5">
                {QUESTIONS.map((q, i) => (
                  <div key={q.key}>
                    <label className={labelCls} style={{ color: NAVY }}>
                      <span style={{ color: ORANGE }}>{String(i + 1).padStart(2, "0")} · </span>
                      {q.label}
                      {!q.required && <span className="normal-case font-normal" style={{ color: "#94A3B8" }}> (optional)</span>}
                    </label>
                    {q.input ? (
                      <input value={form[q.key]} onChange={set(q.key)} required={q.required}
                             maxLength={200} placeholder={q.placeholder}
                             className="mt-2 w-full border-2 p-3 text-sm outline-none" style={inputStyle}
                             data-testid={`early-access-q-${q.key}`} />
                    ) : (
                      <textarea value={form[q.key]} onChange={set(q.key)} required={q.required}
                                rows={q.rows} maxLength={3000} placeholder={q.placeholder}
                                className="mt-2 w-full border-2 p-3 text-sm outline-none" style={inputStyle}
                                data-testid={`early-access-q-${q.key}`} />
                    )}
                  </div>
                ))}

                <div>
                  <label className={labelCls} style={{ color: NAVY }}>
                    <span style={{ color: ORANGE }}>07 · </span>
                    Would you like early access to RESMO?
                  </label>
                  <div className="flex gap-2 mt-2" data-testid="early-access-wants-access">
                    {[{ v: true, l: "Yes — save my seat" }, { v: false, l: "Just sharing my story" }].map(({ v, l }) => (
                      <button type="button" key={String(v)} onClick={() => setWantsAccess(v)}
                              className="px-4 py-2 rounded-full border-2 text-xs font-bold transition-colors"
                              style={{
                                background: wantsAccess === v ? NAVY : "white",
                                color: wantsAccess === v ? YELLOW : NAVY,
                                borderColor: NAVY,
                              }}
                              data-testid={`early-access-access-${v ? "yes" : "no"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} style={{ color: NAVY }}>
                      <span style={{ color: ORANGE }}>08 · </span>Name
                    </label>
                    <input value={form.name} onChange={set("name")} required maxLength={120}
                           placeholder="Alex Rivera"
                           className="mt-2 w-full border-2 p-3 text-sm outline-none" style={inputStyle}
                           data-testid="early-access-name-input" />
                  </div>
                  <div>
                    <label className={labelCls} style={{ color: NAVY }}>Email</label>
                    <input type="email" value={form.email} onChange={set("email")} required
                           placeholder="you@example.com"
                           className="mt-2 w-full border-2 p-3 text-sm outline-none" style={inputStyle}
                           data-testid="early-access-email-input" />
                  </div>
                </div>

                <div>
                  <label className={labelCls} style={{ color: NAVY }}>
                    LinkedIn profile <span className="normal-case font-normal" style={{ color: "#94A3B8" }}>(optional)</span>
                  </label>
                  <input value={form.linkedin_url} onChange={set("linkedin_url")} maxLength={300}
                         placeholder="https://linkedin.com/in/you"
                         className="mt-2 w-full border-2 p-3 text-sm outline-none" style={inputStyle}
                         data-testid="early-access-linkedin-input" />
                </div>
              </div>

              {error && (
                <div className="mt-4 border-2 p-3 text-sm font-bold"
                     style={{ borderColor: "#B91C1C", background: "#FEE2E2", color: "#B91C1C" }}
                     data-testid="early-access-error">
                  {error}
                </div>
              )}

              <label className="mt-5 flex items-start gap-3 cursor-pointer select-none" data-testid="early-access-share-consent">
                <input type="checkbox" checked={shareOk} onChange={(e) => setShareOk(e.target.checked)}
                       className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#FF6A00]"
                       data-testid="early-access-share-checkbox" />
                <span className="text-xs leading-relaxed" style={{ color: "#475569" }}>
                  It's OK to share an <strong>anonymized snippet</strong> of my story on this page
                  (first name only — never my email or full name).
                </span>
              </label>

              <button type="submit" disabled={submitting}
                      className="mt-6 w-full py-3.5 border-2 font-black text-sm uppercase tracking-[1.5px] transition-transform active:translate-y-0.5 disabled:opacity-60"
                      style={{ background: ORANGE, borderColor: NAVY, color: "white", boxShadow: `4px 4px 0 0 ${NAVY}` }}
                      data-testid="early-access-submit-btn">
                {submitting ? (
                  <span className="inline-flex items-center gap-2"><Loader size={14} className="animate-spin" /> Sending…</span>
                ) : (
                  "Share My Story"
                )}
              </button>
              <p className="mt-3 text-center text-[11px]" style={{ color: "#94A3B8" }}>
                No spam. One invite email when your seat opens. Your story stays private unless you tick the share box above.
              </p>
            </form>
          )}
        </div>

        {/* Story wall — anonymized, consented, admin-curated */}
        {wall.length > 0 && (
          <div className="mt-14" data-testid="early-access-story-wall">
            <div className="text-center mb-6">
              <h2 className="text-lg md:text-lg font-black" style={{ color: NAVY }}>
                You're not alone
              </h2>
              <p className="text-sm mt-1" style={{ color: "#64748B" }}>
                Real stories from people already in the movement.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {visibleWall.map((w, i) => (
                <div key={`${wallOffset}-${w.name}-${i}`} className="bg-white border-[3px] p-5"
                     style={{ borderColor: NAVY, boxShadow: `5px 5px 0 0 ${NAVY}`, animation: "rmPopIn 0.5s ease" }}
                     data-testid={`early-access-wall-card-${i}`}>
                  <Quote size={20} style={{ color: ORANGE }} className="mb-2" />
                  {w.question_label && (
                    <div className="font-mono-rm text-[9px] uppercase tracking-[2px] mb-2" style={{ color: "#94A3B8" }}>
                      {w.question_label}
                    </div>
                  )}
                  <p className="text-sm italic leading-relaxed" style={{ color: "#334155" }}>
                    {w.snippet}
                  </p>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <div className="font-bold text-xs" style={{ color: NAVY }}>— {w.name}</div>
                    {w.id && (
                      <a href={`${API}/early-access/wall-card/${w.id}.png`}
                         download={`resmo-story-${(w.name || "card").replace(/\W+/g, "-")}.png`}
                         target="_blank" rel="noreferrer"
                         title="Download this story as a shareable image for LinkedIn"
                         className="inline-flex items-center gap-1 font-mono-rm text-[9px] uppercase tracking-[1px] font-bold hover:underline"
                         style={{ color: ORANGE }}
                         data-testid={`early-access-wall-share-${i}`}>
                        <Download size={11} /> Share card
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Closing — SPARK */}
        <div className="mt-16 text-center" data-testid="early-access-closing">
          <img
            src="/brand/resmo-welcoming.png"
            alt="RESMO"
            className="w-16 mx-auto mb-4 select-none"
            draggable={false}
            data-testid="early-access-mascot"
          />
          <div className="text-xl sm:text-2xl font-black leading-snug" style={{ color: NAVY }}>
            Beyond the Resume.<br />Be Seen. Be Understood. Be Discovered.
          </div>
          <div className="mt-4 inline-flex items-center gap-2 font-bold text-sm italic" style={{ color: ORANGE }}>
            <Flame size={16} /> Every career begins with a SPARK.
          </div>
          <div className="mt-8 font-mono-rm text-[10px] uppercase tracking-[2px]" style={{ color: "#94A3B8" }}>
            RESMO · Coming soon · Join the movement
          </div>
        </div>
      </div>
    </div>
  );
}
